import {lazy} from 'react';

const RELOAD_FLAG = 'pbss:chunk-reload';

// A chunk goes missing when a new deploy replaces the files behind a tab that
// is already open. Both webpack (JS) and mini-css-extract (CSS) errors count.
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  error?.code === 'CSS_CHUNK_LOAD_FAILED' ||
  /Loading (CSS )?chunk [\w-]+ failed/i.test(error?.message || '');

const hasReloaded = () => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1';
  } catch (error) {
    return true; // storage unavailable: never auto-reload, so no loop is possible
  }
};

const setReloaded = (value) => {
  try {
    if (value) {
      sessionStorage.setItem(RELOAD_FLAG, '1');
    } else {
      sessionStorage.removeItem(RELOAD_FLAG);
    }
  } catch (error) {
    // storage unavailable: nothing to remember
  }
};

/**
 * React.lazy() that survives deploys: if a page's chunk no longer exists,
 * reload once to pick up the new index.html. A second failure is left to
 * ChunkErrorBoundary, so the user can never get stuck in a reload loop.
 */
const lazyWithRetry = (importPage) =>
  lazy(async () => {
    try {
      const page = await importPage();
      setReloaded(false);
      return page;
    } catch (error) {
      // Offline, a reload would only show the browser's offline page; let
      // ChunkErrorBoundary offer a retry instead.
      const isOffline = navigator.onLine === false;
      if (isChunkLoadError(error) && !hasReloaded() && !isOffline) {
        setReloaded(true);
        window.location.reload();
        return new Promise(() => {}); // stay suspended until the reload happens
      }
      throw error;
    }
  });

export default lazyWithRetry;
